import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import PlantillaScreen from "../helpers/PlantillaScreen";
import { LoaderScreen } from "../components/LoaderScreen";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import GridOnIcon from "@mui/icons-material/GridOn";
import Swal from "sweetalert2";
import request from "../../Helpers/API";
import { FormatMoney } from "../../Helpers/FormatNumber";
import { useBox } from "../CreditoyCobranza/DashboardCobranza/helper/useBox";
import { GetKey } from "../CreditoyCobranza/ReporteCobranza/Actions/GetContainer";
import { AgruparByProp } from "../../Helpers/Agrupaciones/AgruparByProp";
import FiltroMultiple from "../Postventa/Dashboard/Modales/FiltroMultiple";
import FiltroCarteraTransito from "./FiltroCarteraTransito";

// Columnas de la tabla (confirmadas con el endpoint real). "sucursal",
// "serie", "modulo", "fecha" y "vencimiento" usan FiltroMultiple.
const CAMPOS_FILTRO_MULTIPLE = ["sucursal", "serie", "modulo", "fecha", "vencimiento"];
const TOTAL_COLUMNAS = 9; // cliente, sucursal, documento, modulo, folio, serie, saldo, fecha, vencimiento
// colSpan de filas de origen/vacío = TOTAL_COLUMNAS + 2 (col-toggle + col-estatus, que no cuentan aquí).

// Mismas columnas visibles en la tabla, usadas únicamente para armar el
// Excel exportado (no para el render de la tabla, que se deja explícito).
const COLUMNAS_EXPORTAR = [
    { campo: "estatus", etiqueta: "Estatus", esEstatus: true },
    { campo: "cliente", etiqueta: "Cliente" },
    { campo: "sucursal", etiqueta: "Sucursal" },
    { campo: "documento", etiqueta: "Documento" },
    { campo: "modulo", etiqueta: "Módulo" },
    { campo: "folio", etiqueta: "Folio" },
    { campo: "serie", etiqueta: "Serie" },
    { campo: "saldo", etiqueta: "Saldo", esImporte: true },
    { campo: "fecha", etiqueta: "Fecha" },
    { campo: "vencimiento", etiqueta: "Vencimiento" },
];

const estadoFiltrosVacio = () => Object.fromEntries(CAMPOS_FILTRO_MULTIPLE.map((campo) => [campo, []]));
const estadoMostrarFiltroVacio = () =>
    Object.fromEntries(CAMPOS_FILTRO_MULTIPLE.map((campo) => [campo, { open: false }]));

// Escapa/entrecomilla un valor para CSV (RFC 4180): evita que comas,
// comillas o saltos de línea dentro del dato rompan las columnas.
const escaparCsv = (valor) => {
    if (valor === undefined || valor === null) return '""';
    const texto = String(valor).replace(/"/g, '""');
    return `"${texto}"`;
};

const CarteraTransitoMainScreen = () => {
    const history = useHistory();
    const [boxstate, setBoxState] = useBox();
    const [data, setData] = useState([]);

    const [seccionesColapsadas, setSeccionesColapsadas] = useState({});

    // Filtros por columna (FiltroMultiple), mismo esquema usado en GarantiaMainScreen.
    const [filtros, setFiltros] = useState(estadoFiltrosVacio);
    const [filtrosTemp, setFiltrosTemp] = useState(estadoFiltrosVacio);
    const [mostrarFiltro, setMostrarFiltro] = useState(estadoMostrarFiltroVacio);
    const [prioridadFiltros, setPrioridadFiltros] = useState([]);

    const [loader, setLoader] = useState({
        mostrar: false,
        alerta: false,
        error: false,
        mensaje: "",
    });

    // Cada vez que llega un nuevo listado se reinician filtros/colapsados,
    // para no arrastrar selecciones que ya no apliquen a los datos nuevos.
    useEffect(() => {
        setFiltros(estadoFiltrosVacio());
        setFiltrosTemp(estadoFiltrosVacio());
        setMostrarFiltro(estadoMostrarFiltroVacio());
        setPrioridadFiltros([]);
        setSeccionesColapsadas({});
    }, [data]);

    const actualizarPrioridad = (filtro) => {
        setPrioridadFiltros((prev) => {
            const index = prev.indexOf(filtro);
            let nuevasPrioridades = index === -1 ? [...prev, filtro] : prev.slice(0, index + 1);

            setFiltros((prevFiltros) => {
                const limpio = { ...prevFiltros };
                Object.keys(limpio).forEach((k) => {
                    if (!nuevasPrioridades.includes(k)) limpio[k] = [];
                });
                return limpio;
            });

            setFiltrosTemp((prevTemp) => {
                const limpio = { ...prevTemp };
                Object.keys(limpio).forEach((k) => {
                    if (!nuevasPrioridades.includes(k)) limpio[k] = [];
                });
                return limpio;
            });

            return nuevasPrioridades;
        });
    };

    const aplicarFiltros = (registros, filtrosAplicar, camposActivos) => {
        let resultado = [...registros];
        camposActivos.forEach((campo) => {
            if (filtrosAplicar[campo]?.length > 0) {
                resultado = resultado.filter((item) => filtrosAplicar[campo].includes(item[campo]));
            }
        });
        return resultado;
    };

    const datosFiltrados = useMemo(
        () => aplicarFiltros(data, filtros, prioridadFiltros),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [data, filtros, prioridadFiltros]
    );

    // Opciones de cada FiltroMultiple, en cascada según la prioridad de aplicación
    // (misma lógica que GarantiaMainScreen: cada filtro ofrece opciones en base
    // a los filtros aplicados ANTES que él).
    const opcionesPorColumna = useMemo(() => {
        const getBaseData = (campo) => {
            const index = prioridadFiltros.indexOf(campo);
            if (index === -1) return aplicarFiltros(data, filtros, prioridadFiltros);
            if (index === 0) return data;
            return aplicarFiltros(data, filtros, prioridadFiltros.slice(0, index));
        };

        const opciones = {};
        CAMPOS_FILTRO_MULTIPLE.forEach((campo) => {
            const base = getBaseData(campo);
            opciones[campo] = [...new Set(base.map((item) => item[campo]).filter((v) => v !== undefined && v !== null && v !== ""))]
                .map((valor) => ({ value: valor, label: String(valor) }));
        });
        return opciones;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, filtros, prioridadFiltros]);

    // Agrupación por "origen": cada grupo se muestra como una sección con
    // encabezado propio y puede colapsarse/expandirse de forma independiente.
    const gruposPorOrigen = useMemo(() => {
        const origenes = AgruparByProp(datosFiltrados, "origen");
        return origenes.map((origen) => ({
            origen,
            registros: datosFiltrados.filter((item) => item.origen === origen),
        }));
    }, [datosFiltrados]);

    // KPIs del resumen superior: total de registros con su monto, y
    // documentos sin resolver/resueltos (según "item.estatus", igual que el
    // punto de la tabla) con su monto. Se calculan sobre "datosFiltrados"
    // para reflejar los FiltroMultiple activos.
    const kpis = useMemo(() => {
        let montoTotal = 0;
        let sinResolver = 0;
        let montoSinResolver = 0;
        let resueltos = 0;
        let montoResueltos = 0;

        datosFiltrados.forEach((item) => {
            const saldo = item.saldo || 0;
            montoTotal += saldo;

            if (item.estatus) {
                sinResolver += 1;
                montoSinResolver += saldo;
            } else {
                resueltos += 1;
                montoResueltos += saldo;
            }
        });

        return { montoTotal, sinResolver, montoSinResolver, resueltos, montoResueltos };
    }, [datosFiltrados]);

    const toggleSeccion = (origen) => {
        setSeccionesColapsadas((prev) => ({ ...prev, [origen]: !prev[origen] }));
    };

    const expandirTodo = () => setSeccionesColapsadas({});
    const colapsarTodo = () => {
        setSeccionesColapsadas(Object.fromEntries(gruposPorOrigen.map((g) => [g.origen, true])));
    };

    // Genera un CSV real (separador ",") con las mismas columnas de la
    // tabla y los registros actualmente filtrados (respeta los
    // FiltroMultiple activos), agrupados por "origen" igual que en pantalla:
    // antes de los registros de cada grupo se inserta una fila con el
    // nombre del origen (y su cantidad), para identificarlos fácilmente.
    // Se usa CSV y no un .xls "disfrazado" de HTML para que el formato del
    // archivo coincida con su extensión y Excel lo abra sin advertencias.
    // Cada campo va entrecomillado (escaparCsv), así que las comas de
    // miles de FormatMoney no rompen las columnas.
    const onExportarExcel = () => {
        if (datosFiltrados.length === 0) {
            Swal.fire("CARTERA EN TRÁNSITO", "No hay información para exportar.", "info");
            return;
        }

        const encabezados = COLUMNAS_EXPORTAR.map((col) => escaparCsv(col.etiqueta)).join(",");

        const filas = [];
        gruposPorOrigen.forEach((grupo) => {
            const filaOrigen = COLUMNAS_EXPORTAR.map((col, index) =>
                index === 0 ? escaparCsv(`${grupo.origen || "Sin origen"} (${grupo.registros.length})`) : escaparCsv("")
            );
            filas.push(filaOrigen.join(","));

            grupo.registros.forEach((item) => {
                const celdas = COLUMNAS_EXPORTAR.map((col) => {
                    let valor = item[col.campo];
                    if (col.esImporte) valor = FormatMoney(valor || 0);
                    else if (col.esEstatus) valor = valor ? "Por resolver" : "Resuelto";
                    return escaparCsv(valor);
                });
                filas.push(celdas.join(","));
            });
        });

        const csv = [encabezados, ...filas].join("\r\n");

        // BOM UTF-8 para que Excel muestre bien acentos/ñ.
        const blob = new Blob([String.fromCharCode(0xfeff), csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const enlace = document.createElement("a");
        enlace.href = url;
        enlace.download = `CarteraTransito_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(enlace);
        enlace.click();
        document.body.removeChild(enlace);
        URL.revokeObjectURL(url);
    };

    useEffect(() => {
        onExecute(boxstate.ejercicio, boxstate.periodo, boxstate.pv.sucursal, boxstate.pv.adr).catch(() => { });
    }, []);


    // Consulta el listado de Cartera en Tránsito.
    const onExecute = (ejercicio, periodo, sucursal, adr, pendientes = 0) => {
        setLoader({ mostrar: true, alerta: false, error: false, mensaje: "" });

        let sucursalesEnviar = GetKey(sucursal, ["idsucursal"]);
        let regionEnviar = GetKey(adr, ["idadr"]);

        if (!regionEnviar) {
            regionEnviar = "0";
        }

        if (!sucursalesEnviar) {
            sucursalesEnviar = "0";
        }

        const url = `CarteraTransito/listado?ejercicio=${ejercicio}&periodo=${periodo}&sucursal=${sucursalesEnviar}&adr=${regionEnviar}&pendientes=${pendientes}`;

        return new Promise((resolve, reject) => {
            request
                .get(url)
                .then((result) => {
                    setLoader({ mostrar: false, alerta: false, error: false, mensaje: "" });
                    setData(Array.isArray(result) ? result : []);
                    resolve(result);
                })
                .catch((error) => {
                    setLoader({ mostrar: false, alerta: false, error: false, mensaje: "" });
                    // Detalle técnico solo en consola (no se muestra al usuario):
                    // revisar aquí el status/mensaje real que regresa el backend.
                    // console.error("[CarteraTransito] Error en", url, ":", error);
                    Swal.fire(
                        "CARTERA EN TRÁNSITO",
                        "No fue posible completar la operación. Intente nuevamente o contacte al administrador.",
                        "error"
                    );
                    reject(error);
                });
        });
    };


    const onActualizar = () => {
        onExecute(boxstate.ejercicio, boxstate.periodo, boxstate.pv.sucursal, boxstate.pv.adr).catch(() => { });
    };

    // Dispara CarteraTransito/actualizar. "ejercicio"/"periodo" van un mes
    // ANTES del mes elegido en boxstate (si periodo=1, se manda periodo=12
    // del ejercicio anterior); "ejerciciotransito"/"periodotransito" son
    // los valores tal cual están en boxstate (el mes elegido).
    const onActualizarCarteraTransito = () => {
        const periodoTransito = Number(boxstate.periodo);
        const ejercicioTransito = Number(boxstate.ejercicio);

        const periodoAnterior = periodoTransito === 1 ? 12 : periodoTransito - 1;
        const ejercicioAnterior = periodoTransito === 1 ? ejercicioTransito - 1 : ejercicioTransito;

        setLoader({ mostrar: true, alerta: false, error: false, mensaje: "" });

        const url = `CarteraTransito/actualizar?ejercicio=${ejercicioAnterior}&periodo=${periodoAnterior}&ejerciciotransito=${ejercicioTransito}&periodotransito=${periodoTransito}`;

        request
            .get(url)
            .then((result) => {
                setLoader({ mostrar: false, alerta: false, error: false, mensaje: "" });
                Swal.fire("CARTERA EN TRÁNSITO", "La actualización se ejecutó correctamente.", "success").then(() => {
                    onExecute(boxstate.ejercicio, boxstate.periodo, boxstate.pv.sucursal, boxstate.pv.adr).catch(() => { });
                });
            })
            .catch((error) => {
                setLoader({ mostrar: false, alerta: false, error: false, mensaje: "" });
                // Detalle técnico solo en consola (no se muestra al usuario).
                // console.error("[CarteraTransito] Error en", url, ":", error);
                Swal.fire(
                    "CARTERA EN TRÁNSITO",
                    "No fue posible completar la actualización. Intente nuevamente o contacte al administrador.",
                    "error"
                );
            });
    };

    const totalRegistros = datosFiltrados.length;

    return (
        <PlantillaScreen
            titulo={"CARTERA EN TRÁNSITO"}
            className_main={1}
            className={"cartera-transito-main-screen"}
        >
            <LoaderScreen loader={loader} />
            <div className="cartera-transito-actions">
                <div className="cartera-transito-actions-regresar" onClick={() => history.goBack()}>
                    <ArrowBackIcon /> Regresar
                </div>
                <div className="cartera-transito-actions-filtro">
                    <FiltroCarteraTransito
                        onExecute={onExecute}
                        boxState={boxstate}
                        onBoxChange={setBoxState}
                    />
                </div>
                {/* <div className="cartera-transito-actions-actualizar" onClick={onActualizar}>
                    <RefreshIcon /> Actualizar
                </div> */}
                {gruposPorOrigen.length > 0 && (
                    <>
                        {/* <div className="cartera-transito-actions-secundaria" onClick={expandirTodo}>
                            <UnfoldMoreIcon /> Expandir todo
                        </div>
                        <div className="cartera-transito-actions-secundaria" onClick={colapsarTodo}>
                            <UnfoldLessIcon /> Colapsar todo
                        </div> */}
                        <div className="cartera-transito-actions-excel" onClick={onExportarExcel}>
                            <GridOnIcon /> Excel
                        </div>
                        <div className="cartera-transito-actions-actualizar-transito" onClick={onActualizarCarteraTransito}>
                            <RefreshIcon /> Actualizar
                        </div>
                    </>
                )}
            </div>

            <div className="cartera-transito-resumen">
                <div className="cartera-transito-kpi">
                    <div className="kpi-card kpi-card-neutro">
                        <span className="kpi-card-valor">{totalRegistros}</span>
                        <div className="kpi-card-info">
                            <span className="kpi-card-etiqueta">Registros</span>
                            <span className="kpi-card-monto">${FormatMoney(kpis.montoTotal)}</span>
                        </div>
                    </div>
                    <div className="kpi-card kpi-card-rojo">
                        <span className="kpi-card-valor">{kpis.sinResolver}</span>
                        <div className="kpi-card-info">
                            <span className="kpi-card-etiqueta">Sin resolver</span>
                            <span className="kpi-card-monto">${FormatMoney(kpis.montoSinResolver)}</span>
                        </div>
                    </div>
                    <div className="kpi-card kpi-card-verde">
                        <span className="kpi-card-valor">{kpis.resueltos}</span>
                        <div className="kpi-card-info">
                            <span className="kpi-card-etiqueta">Resueltos</span>
                            <span className="kpi-card-monto">${FormatMoney(kpis.montoResueltos)}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="cartera-transito-table scroll">
                <table cellPadding={0} cellSpacing={0}>
                    <thead>
                        <tr>
                            <th className="col-toggle"></th>
                            <th className="col-estatus"></th>
                            <th>
                                <div>Cliente</div>
                            </th>
                            <th>
                                <FiltroMultiple
                                    label="Sucursal"
                                    field="sucursal"
                                    options={opcionesPorColumna.sucursal}
                                    filtros={filtros}
                                    setFiltros={setFiltros}
                                    filtrosTemp={filtrosTemp}
                                    setFiltrosTemp={setFiltrosTemp}
                                    mostrarFiltro={mostrarFiltro}
                                    setMostrarFiltro={setMostrarFiltro}
                                    actualizarPrioridad={actualizarPrioridad}
                                    setPrioridadFiltros={setPrioridadFiltros}
                                />
                            </th>
                            <th>
                                <div>Documento</div>
                            </th>
                            <th>
                                <FiltroMultiple
                                    label="Módulo"
                                    field="modulo"
                                    options={opcionesPorColumna.modulo}
                                    filtros={filtros}
                                    setFiltros={setFiltros}
                                    filtrosTemp={filtrosTemp}
                                    setFiltrosTemp={setFiltrosTemp}
                                    mostrarFiltro={mostrarFiltro}
                                    setMostrarFiltro={setMostrarFiltro}
                                    actualizarPrioridad={actualizarPrioridad}
                                    setPrioridadFiltros={setPrioridadFiltros}
                                />
                            </th>
                            <th>
                                <div>Folio</div>
                            </th>
                            <th>
                                <FiltroMultiple
                                    label="Serie"
                                    field="serie"
                                    options={opcionesPorColumna.serie}
                                    filtros={filtros}
                                    setFiltros={setFiltros}
                                    filtrosTemp={filtrosTemp}
                                    setFiltrosTemp={setFiltrosTemp}
                                    mostrarFiltro={mostrarFiltro}
                                    setMostrarFiltro={setMostrarFiltro}
                                    actualizarPrioridad={actualizarPrioridad}
                                    setPrioridadFiltros={setPrioridadFiltros}
                                />
                            </th>
                            <th>
                                <div>Saldo</div>
                            </th>
                            <th>
                                <FiltroMultiple
                                    label="Fecha"
                                    field="fecha"
                                    options={opcionesPorColumna.fecha}
                                    filtros={filtros}
                                    setFiltros={setFiltros}
                                    filtrosTemp={filtrosTemp}
                                    setFiltrosTemp={setFiltrosTemp}
                                    mostrarFiltro={mostrarFiltro}
                                    setMostrarFiltro={setMostrarFiltro}
                                    actualizarPrioridad={actualizarPrioridad}
                                    setPrioridadFiltros={setPrioridadFiltros}
                                />
                            </th>
                            <th>
                                <FiltroMultiple
                                    label="Vencimiento"
                                    field="vencimiento"
                                    options={opcionesPorColumna.vencimiento}
                                    filtros={filtros}
                                    setFiltros={setFiltros}
                                    filtrosTemp={filtrosTemp}
                                    setFiltrosTemp={setFiltrosTemp}
                                    mostrarFiltro={mostrarFiltro}
                                    setMostrarFiltro={setMostrarFiltro}
                                    actualizarPrioridad={actualizarPrioridad}
                                    setPrioridadFiltros={setPrioridadFiltros}
                                />
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {gruposPorOrigen.length === 0 && (
                            <tr className="fila-sin-datos">
                                <td colSpan={TOTAL_COLUMNAS + 2}>Sin movimientos de cartera en tránsito para los filtros seleccionados.</td>
                            </tr>
                        )}
                        {gruposPorOrigen.map((grupo) => {
                            const colapsado = !!seccionesColapsadas[grupo.origen];
                            return (
                                <React.Fragment key={grupo.origen}>
                                    <tr className="fila-origen" onClick={() => toggleSeccion(grupo.origen)}>
                                        <td colSpan={TOTAL_COLUMNAS + 2}>
                                            <div className="fila-origen-contenido">
                                                <ExpandMoreIcon className={`icono-toggle${colapsado ? " colapsado" : ""}`} />
                                                <span className="fila-origen-titulo">{grupo.origen || "Sin origen"}</span>
                                                <span className="fila-origen-contador">{grupo.registros.length}</span>
                                            </div>
                                        </td>
                                    </tr>
                                    {!colapsado &&
                                        grupo.registros.map((item, index) => (
                                            <tr key={`${grupo.origen}-${index}`}>
                                                <td className="col-toggle"></td>
                                                <td className="col-estatus">
                                                    <span
                                                        className={`estatus-punto ${item.estatus ? "estatus-punto-rojo" : "estatus-punto-verde"}`}
                                                        title={item.estatus ? "Con incidencia" : "Sin incidencia"}
                                                    ></span>
                                                </td>
                                                <td className="col-izquierda col-cliente" title={item.cliente}>{item.cliente || "-"}</td>
                                                <td className="col-izquierda">{item.sucursal || "-"}</td>
                                                <td className="col-izquierda">{item.documento || "-"}</td>
                                                <td className="col-izquierda">{item.modulo || "-"}</td>
                                                <td className="col-izquierda">{item.folio || "-"}</td>
                                                <td className="col-izquierda">{item.serie || "-"}</td>
                                                <td className="col-derecha">${FormatMoney(item.saldo || 0)}</td>
                                                <td className="col-izquierda">{item.fecha || "-"}</td>
                                                <td className="col-izquierda">{item.vencimiento || "-"}</td>
                                            </tr>
                                        ))}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </PlantillaScreen>
    );
};

export default CarteraTransitoMainScreen;
